import Promise from 'bluebird';
import _ from 'underscore';
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { StockImageView } from '@bingads-webui-campaign-react/add-stock-images-view';
import { constants } from '@bingads-webui-campaign-react/image-experience-model';
import { ROOT_FOLDER_ID, MaxAddAssetJobInParell } from '@bingads-webui-campaign-react/asset-library-model';
import { VIEWS } from '../view-type';

const ManagedStockImageView = ({
  stockImageConfig,
  setShowBlocker,
  perfMarker,
  assetLibraryConfig,
  assetLibraryDataService,
  logger,
  maxUploadNum,
  setShowApplyButton,
  triggerAddImagesRef,
  setSelectionInfoText,
  setDisableApplyButton,
  setErrors,
  i18n,
}) => {
  const {
    onImageSelected,
    onImagesSelected,
    onImageAddError,
    enableStockMultipleSelection: enableMultipleSelection,
  } = assetLibraryConfig;

  const onChangeImageIdSelection = useCallback((args) => {
    const stockImagePerfMarker = perfMarker.createChild('stockImagePicker');
    stockImagePerfMarker.willInit();

    const selectStockImage = assetLibraryDataService.uploadStockImage({
      stockImageId: args.selectedImageId,
      folderId: ROOT_FOLDER_ID,
    })
      .then(value => onImageSelected({ id: value }, constants.imageSources.Stock))
      .catch(e => onImageAddError(e));

    setShowBlocker(true);
    selectStockImage
      .tap(() => {
        const { selectedImageId: imageId } = args || {};
        logger.logTrace('Ad creation flow - selected stock image', { imageId });
      })
      .finally(() => {
        setShowBlocker(false);
        stockImagePerfMarker.done();
      });
  }, [perfMarker, setShowBlocker, assetLibraryDataService, onImageSelected, onImageAddError, logger]);

  const [images, setImages] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [skip, setSkip] = useState(0);
  const [serializedFilterForComp, setSerializedFilterForComp] = useState('');
  const [isLoadingFirstPage, setIsLoadingFirstPage] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState({});

  useEffect(() => {
    // init the state for multiple selection
    if (enableMultipleSelection) {
      setShowApplyButton(true);
      setDisableApplyButton(true);
      setErrors([]);
      setSelectionInfoText(i18n.getString(_TL_('0 of {{limitNumber}} images selected'), { limitNumber: maxUploadNum }));
    }
  }, [enableMultipleSelection, i18n, maxUploadNum, setDisableApplyButton, setErrors, setSelectionInfoText, setShowApplyButton]);

  useEffect(() => {
    const selectedImages = _.filter(images, image => image && image.isSelected) || [];
    if (selectedImages.length > maxUploadNum) {
      setErrors([i18n.getString(_TL_('You\'ve exceeded the max. number of assets you can add at a time ({{limitNumber}}). Please try again.'), { limitNumber: maxUploadNum })]);
      setDisableApplyButton(true);
    } else if (selectedImages.length > 0) {
      setErrors([]);
      setDisableApplyButton(false);
    } else {
      setErrors([]);
      setDisableApplyButton(true);
    }

    const singularImageString = _TL_('{{selectedNumber}} of {{limitNumber}} image selected');
    const pluralImagesString = _TL_('{{selectedNumber}} of {{limitNumber}} images selected');
    const stringToUse = maxUploadNum > 1 ? pluralImagesString : singularImageString;
    setSelectionInfoText(i18n.getString(stringToUse, { selectedNumber: selectedImages.length, limitNumber: maxUploadNum }));
  }, [enableMultipleSelection, i18n, images, maxUploadNum, setDisableApplyButton, setErrors, setSelectionInfoText]);

  // only for multiple selection mode
  const onApplyButtonClick = useCallback(() => {
    const selectedImages = _.filter(images, image => image && image.isSelected) || [];

    setShowBlocker(true);

    const stockImagePerfMarker = perfMarker.createChild('stockImagePicker');
    stockImagePerfMarker.willInit();

    Promise.map(
      selectedImages,
      (img) => {
        const { id } = img || {};
        return assetLibraryDataService.uploadStockImage({ stockImageId: id, folderId: ROOT_FOLDER_ID });
      },
      { concurrency: MaxAddAssetJobInParell }
    )
      .then((imageIds) => {
        logger.logTrace('Ad creation flow - selected stock images', { imageIds });
        return onImagesSelected(imageIds, constants.imageSources.Stock);
      })
      .catch((err) => {
        onImageAddError(err);
      })
      .finally(() => {
        setShowBlocker(false);
        stockImagePerfMarker.done();
      });
  }, [assetLibraryDataService, images, logger, onImageAddError, onImagesSelected, perfMarker, setShowBlocker]);

  // listen to the apply button click for multiple selection
  // eslint-disable-next-line no-param-reassign
  triggerAddImagesRef.current = enableMultipleSelection ? onApplyButtonClick : undefined;

  return (
    <StockImageView
      {...stockImageConfig}
      enableMultipleSelection={enableMultipleSelection}
      onChangeImageIdSelection={enableMultipleSelection ? _.noop : onChangeImageIdSelection}
      imageCellWidth={202}
      imageCellHeight={202}
      images={images}
      setImages={setImages}
      hasMore={hasMore}
      setHasMore={setHasMore}
      skip={skip}
      setSkip={setSkip}
      serializedFilterForComp={serializedFilterForComp}
      setSerializedFilterForComp={setSerializedFilterForComp}
      isLoadingFirstPage={isLoadingFirstPage}
      setIsLoadingFirstPage={setIsLoadingFirstPage}
      searchQuery={searchQuery}
      setSearchQuery={setSearchQuery}
      filter={filter}
      setFilter={setFilter}
    />
  );
};

ManagedStockImageView.propTypes = {
  stockImageConfig: PropTypes.shape({
    getStockImages: PropTypes.func,
  }).isRequired,
  setShowBlocker: PropTypes.func,
  perfMarker: PropTypes.shape({
    willFetchFile: PropTypes.func.isRequired,
    willInit: PropTypes.func.isRequired,
    willFetchData: PropTypes.func.isRequired,
    willRender: PropTypes.func.isRequired,
    done: PropTypes.func.isRequired,
    createChild: PropTypes.func.isRequired,
  }).isRequired,
  assetLibraryDataService: PropTypes.shape({}).isRequired,
  assetLibraryConfig: PropTypes.shape({
    onImageSelected: PropTypes.func,
    onImagesSelected: PropTypes.func,
    onImageAddError: PropTypes.func,
  }).isRequired,
  logger: PropTypes.shape({
    logTrace: PropTypes.func,
  }).isRequired,
  triggerAddImagesRef: PropTypes.shape({
    current: PropTypes.func,
  }).isRequired,
  setShowApplyButton: PropTypes.func,
  setSelectionInfoText: PropTypes.func,
  setDisableApplyButton: PropTypes.func,
  setErrors: PropTypes.func,
  maxUploadNum: PropTypes.number,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
};

ManagedStockImageView.defaultProps = {
  setShowBlocker: _.noop,
  setShowApplyButton: _.noop,
  setDisableApplyButton: _.noop,
  setSelectionInfoText: _.noop,
  setErrors: _.noop,
  maxUploadNum: 16,
};

export const stockImageSourceConfig = {
  title: _TL_('Stock images'),
  showBackButton: true,
  onRenderContent: props => (
    <ManagedStockImageView
      {...props}
    />
  ),
  buttonConfig: {
    label: _TL_('Stock images'),
    view: VIEWS.StockImage,
    iconName: 'TripleColumn',
    buttonLocator: 'stock-images',
  },
};
